import { cloneCameraPath, emptyCameraPath, type CameraPath } from "@/lib/location-scene-3d/camera-path"

export const MAX_CAMERA_PATH_TAKES = 32
export const LEGACY_CAMERA_PATH_TAKE_ID = "take_legacy"
const TAKE_ID_RE = /^(?:take_legacy|t_[a-z0-9]{8,32})$/
const TAKE_NAME_RE = /^(?:Дубль|Take) (\d+)$/
const TAKE_NAME_MAX = 120

export type CameraPathTake = {
  id: string
  name: string
  path: CameraPath
}

export type CameraPathTakeSet = {
  takes: CameraPathTake[]
  activeId: string | null
}

export function newCameraPathTakeId(): string {
  const hex = globalThis.crypto?.randomUUID?.().replace(/-/g, "").slice(0, 12)
  return `t_${hex && hex.length >= 8 ? hex : Math.random().toString(16).slice(2, 14)}`
}

/** Пустой первый дубль новой сцены. Имя снаружи: там есть локаль. */
export function openingCameraPathTake(name: string): CameraPathTake {
  const trimmed = name.trim().slice(0, TAKE_NAME_MAX)
  return { id: newCameraPathTakeId(), name: trimmed || "Take 1", path: emptyCameraPath() }
}

export function cameraPathTakesFull(takes: readonly { id: string }[]): boolean {
  return takes.length >= MAX_CAMERA_PATH_TAKES
}

/** Следующий номер после «Дубль N» / «Take N». Переименованные имена номер не занимают. */
export function nextCameraPathTakeNumber(takes: readonly { name: string }[]): number {
  let max = 0
  for (const take of takes) {
    const match = TAKE_NAME_RE.exec(take.name.trim())
    if (!match) continue
    const n = Number(match[1])
    if (Number.isFinite(n) && n > max) max = n
  }
  return max + 1
}

export function cameraPathHasContent(path: CameraPath | null | undefined): boolean {
  if (!path) return false
  if ((path.samples?.length ?? 0) >= 2) return true
  if ((path.authoring?.points.length ?? 0) >= 2) return true
  if ((path.look_keys?.length ?? 0) > 0) return true
  return false
}

function takeName(raw: unknown): string | null {
  if (typeof raw !== "string") return null
  const name = raw.trim()
  if (!name) return null
  return name.slice(0, TAKE_NAME_MAX)
}

function cloneTake(take: CameraPathTake): CameraPathTake {
  return { id: take.id, name: take.name, path: cloneCameraPath(take.path) }
}

function parseTake(
  raw: unknown,
  normalizePath: (path: CameraPath | null | undefined) => CameraPath,
): CameraPathTake | null {
  if (!raw || typeof raw !== "object") return null
  const row = raw as { id?: unknown; name?: unknown; path?: unknown }
  if (typeof row.id !== "string" || !TAKE_ID_RE.test(row.id)) return null
  const name = takeName(row.name)
  if (!name) return null
  return { id: row.id, name, path: normalizePath(row.path as CameraPath) }
}

/**
 * Нет списка — пролёт с содержимым становится одним дублём.
 * Короткий пролёт (меньше двух точек и без взгляда) остаётся в camera_path, список пустой.
 * Список есть — он источник, camera_path берётся из выбранного дубля. Сверх 32 отбрасываются.
 */
function cloneIfPath(raw: CameraPath | null | undefined): CameraPath {
  if (!raw || typeof raw !== "object") return emptyCameraPath()
  return cloneCameraPath(raw)
}

export function normalizeCameraPathTakes(
  rawTakes: unknown,
  rawActiveId: unknown,
  fallbackPath: CameraPath,
  normalizePath: (path: CameraPath | null | undefined) => CameraPath = cloneIfPath,
  nameFor: (n: number) => string = (n) => `Дубль ${n}`,
): CameraPathTakeSet & { cameraPath: CameraPath } {
  if (!Array.isArray(rawTakes)) {
    const path = normalizePath(fallbackPath)
    if (!cameraPathHasContent(path)) return { takes: [], activeId: null, cameraPath: path }
    const take = { id: LEGACY_CAMERA_PATH_TAKE_ID, name: nameFor(1), path }
    return { takes: [take], activeId: take.id, cameraPath: cloneCameraPath(path) }
  }

  const takes: CameraPathTake[] = []
  for (const item of rawTakes) {
    if (takes.length >= MAX_CAMERA_PATH_TAKES) break
    const take = parseTake(item, normalizePath)
    if (take) takes.push(take)
  }
  if (takes.length === 0) return { takes: [], activeId: null, cameraPath: emptyCameraPath() }

  const requested = typeof rawActiveId === "string" ? rawActiveId : ""
  const active = takes.find((take) => take.id === requested) ?? takes[0]
  return {
    takes: takes.map(cloneTake),
    activeId: active.id,
    cameraPath: cloneCameraPath(active.path),
  }
}

export function cloneCameraPathTakeSet(set: CameraPathTakeSet): CameraPathTakeSet {
  return {
    takes: set.takes.map(cloneTake),
    activeId: set.activeId,
  }
}

export type CameraPathHistoryPatch = {
  takeId: string | null
  path: CameraPath
}

/**
 * Линия из истории — только в существующий дубль. Имя не меняется.
 * Без номера дубля — только если дубль один. Иначе список не меняется.
 */
export function restoreCameraPathForHistory(args: {
  takes: readonly CameraPathTake[]
  takeId: string | null
  path: CameraPath
}): (CameraPathTakeSet & { cameraPath: CameraPath }) | null {
  const path = cloneCameraPath(args.path)
  if (args.takeId) {
    const index = args.takes.findIndex((take) => take.id === args.takeId)
    if (index < 0) return null
    const takes = args.takes.map((take, takeIndex) =>
      takeIndex === index ? { ...cloneTake(take), path } : cloneTake(take),
    )
    return { takes, activeId: args.takeId, cameraPath: cloneCameraPath(path) }
  }
  if (args.takes.length !== 1) return null
  const only = args.takes[0]
  if (!only) return null
  return {
    takes: [{ ...cloneTake(only), path }],
    activeId: only.id,
    cameraPath: cloneCameraPath(path),
  }
}

/**
 * Несколько линий подряд. Чужой id пропускается, остальные пишутся.
 * В конце выбран дубль строки, если он ещё есть; иначе тот, что был открыт.
 */
export function restoreCameraPathsForHistory(args: {
  takes: readonly CameraPathTake[]
  patches: readonly CameraPathHistoryPatch[]
  activeId: string | null
  fallbackActiveId?: string | null
}): (CameraPathTakeSet & { cameraPath: CameraPath }) | null {
  let takes = args.takes.map((take) => cloneTake(take))
  let applied = false
  for (const patch of args.patches) {
    const next = restoreCameraPathForHistory({
      takes,
      takeId: patch.takeId,
      path: patch.path,
    })
    if (!next) continue
    takes = next.takes
    applied = true
  }
  const requested = takes.find((take) => take.id === args.activeId)
  if (requested) {
    return { takes, activeId: requested.id, cameraPath: cloneCameraPath(requested.path) }
  }
  if (!applied) return null
  const fallback = args.fallbackActiveId ? takes.find((take) => take.id === args.fallbackActiveId) : undefined
  const active = fallback ?? takes[0]
  if (!active) return null
  return { takes, activeId: active.id, cameraPath: cloneCameraPath(active.path) }
}
